import React, { useEffect, useMemo, useRef, useState } from 'react';
import { formatTopBarClock, getLocalizedAppLabel, normalizeLanguage, t } from '../utils/i18n';
import { normalizeOsMode } from '../utils/os';

const MENU_WINDOW_IDS = ['settings', 'profile', 'resume', 'projects'];
const START_PANEL_SPECIAL_ICON_IDS = {
  auth: 'ai-auth-default',
  chat: 'chat-text-default'
};
const START_PANEL_ICON_FALLBACKS = {
  settings: ['/icons/settings-icon.svg'],
  profile: [],
  resume: ['/icons/resume-icon.svg'],
  projects: ['/icons/projects-folder.svg'],
  github: ['/icons/github-icon.svg']
};
const WINDOWS_START_TABS = ['All', 'Apps', 'Documents', 'Web', 'More'];
const WINDOWS_START_ITEM_META = {
  settings: {
    type: 'System',
    description: 'Theme, language, wallpaper, and desktop shell settings.'
  },
  profile: {
    type: 'Portfolio',
    description: 'Basic info, skills, and introduction for the portfolio.'
  },
  resume: {
    type: 'Portfolio',
    description: 'Career history, education, and structured resume details.'
  },
  projects: {
    type: 'Portfolio',
    description: 'Projects, outcomes, technology stacks, and delivery history.'
  },
  'ai-auth': {
    type: 'Utility',
    description: 'AI Chat Hub status: which chat targets are ready, and where keys and logins are managed.'
  },
  'ai-chat': {
    type: 'Utility',
    description: 'Open the AI chat workspace and continue guided generation.'
  },
  'change-os': {
    type: 'Utility',
    description: 'Switch between Windows, macOS, and Ubuntu desktop shells.'
  }
};

const START_PANEL_ICON_KEYWORD_PATTERN = /^[a-zA-Z0-9-_]+$/;

const renderWindowsStartSymbol = (symbol) => {
  switch (symbol) {
    case 'search':
      return (
        <svg viewBox="0 0 16 16" aria-hidden="true">
          <circle cx="7" cy="7" r="4.5" fill="none" stroke="currentColor" strokeWidth="1.5" />
          <path d="M10.5 10.5L14 14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      );
    case 'sliders':
      return (
        <svg viewBox="0 0 16 16" aria-hidden="true">
          <path d="M2 4h12M2 8h12M2 12h12" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          <circle cx="5" cy="4" r="1.6" fill="currentColor" />
          <circle cx="10.5" cy="8" r="1.6" fill="currentColor" />
          <circle cx="7.5" cy="12" r="1.6" fill="currentColor" />
        </svg>
      );
    case 'ellipsis':
      return (
        <svg viewBox="0 0 16 16" aria-hidden="true">
          <circle cx="3.5" cy="8" r="1.4" fill="currentColor" />
          <circle cx="8" cy="8" r="1.4" fill="currentColor" />
          <circle cx="12.5" cy="8" r="1.4" fill="currentColor" />
        </svg>
      );
    case 'chevron-right':
      return (
        <svg viewBox="0 0 16 16" aria-hidden="true">
          <path d="M6 3.5L10.5 8 6 12.5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case 'chevron-down':
      return (
        <svg viewBox="0 0 16 16" aria-hidden="true">
          <path d="M3.5 6L8 10.5 12.5 6" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case 'open':
      return (
        <svg viewBox="0 0 16 16" aria-hidden="true">
          <path d="M2.5 5.5h4l1.3 1.4h5.7v5.1a1 1 0 0 1-1 1H3.5a1 1 0 0 1-1-1z" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
          <path d="M2.5 5.5v-1a1 1 0 0 1 1-1h2.3l1.2 1.2" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case 'settings':
      return (
        <svg viewBox="0 0 16 16" aria-hidden="true">
          <path d="M8 2.2l1 .4.8-.5 1.4 1.4-.5.8.4 1 1 .5v2l-1 .5-.4 1 .5.8-1.4 1.4-.8-.5-1 .4-.5 1h-2l-.5-1-1-.4-.8.5-1.4-1.4.5-.8-.4-1-1-.5v-2l1-.5.4-1-.5-.8 1.4-1.4.8.5 1-.4.5-1h2z" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" />
          <circle cx="8" cy="8" r="2.2" fill="none" stroke="currentColor" strokeWidth="1.2" />
        </svg>
      );
    case 'spark':
      return (
        <svg viewBox="0 0 16 16" aria-hidden="true">
          <path d="M8 1.8l1.3 3.2 3.2 1.3-3.2 1.3L8 10.8 6.7 7.6 3.5 6.3 6.7 5z" fill="currentColor" />
          <path d="M12 10.2l.6 1.4 1.4.6-1.4.6-.6 1.4-.6-1.4-1.4-.6 1.4-.6z" fill="currentColor" opacity="0.72" />
        </svg>
      );
    case 'github':
      return (
        <svg viewBox="0 0 16 16" aria-hidden="true">
          <path d="M8 1.8a6.2 6.2 0 0 0-2 12.1c.3.1.4-.1.4-.3v-1.1c-1.6.3-1.9-.7-1.9-.7-.2-.6-.6-.8-.6-.8-.5-.3 0-.3 0-.3.6 0 .9.6.9.6.5.8 1.4.6 1.7.5.1-.4.2-.6.4-.8-1.3-.1-2.7-.6-2.7-2.9 0-.6.2-1.2.6-1.6-.1-.1-.3-.8.1-1.7 0 0 .5-.2 1.7.6A6 6 0 0 1 8 5a6 6 0 0 1 1.6.2c1.2-.8 1.7-.6 1.7-.6.4.9.2 1.6.1 1.7.4.4.6 1 .6 1.6 0 2.2-1.3 2.8-2.7 2.9.2.2.4.5.4 1.1v1.7c0 .2.1.4.4.3A6.2 6.2 0 0 0 8 1.8z" fill="currentColor" />
        </svg>
      );
    default:
      return (
        <svg viewBox="0 0 16 16" aria-hidden="true">
          <circle cx="8" cy="8" r="6" fill="none" stroke="currentColor" strokeWidth="1.4" />
        </svg>
      );
  }
};

const renderStartPanelSpecialIcon = (iconValue) => {
  if (iconValue === START_PANEL_SPECIAL_ICON_IDS.auth) {
    return (
      <div className="windows-start-special-icon windows-start-special-icon-auth" aria-hidden="true">
        <div className="windows-start-special-auth-core">
          <span className="windows-start-special-auth-mark">C</span>
        </div>
        <span className="windows-start-special-auth-badge">AI</span>
      </div>
    );
  }

  if (iconValue === START_PANEL_SPECIAL_ICON_IDS.chat) {
    return (
      <div className="windows-start-special-icon windows-start-special-icon-chat" aria-hidden="true">
        <span className="windows-start-special-chat-label">Chat</span>
      </div>
    );
  }

  return null;
};

const TopBar = ({
  creator,
  version,
  theme,
  language,
  osMode,
  onToggleTheme,
  onOpenSettings,
  focusedWindowId,
  activeWindowLabel,
  onProtectedMenuAction,
  hasAuthenticatedAiSession,
  minimizedWindows,
  onRestoreWindow,
  shellShortcutNode,
  shellShortcutItems,
  onOpenApp
}) => {
  const [time, setTime] = useState(new Date());
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [selectedStartItemId, setSelectedStartItemId] = useState('');
  const [batteryLevel, setBatteryLevel] = useState(1);
  const [isCharging, setIsCharging] = useState(false);
  const [wifiLabel, setWifiLabel] = useState('Online');
  const menuRef = useRef(null);
  const activeLanguage = normalizeLanguage(language);
  const resolvedOsMode = normalizeOsMode(osMode);
  const isDarkMode = theme === 'dark';
  const logoSrc = isDarkMode ? '/assets/devfolio_white_logo.png' : '/assets/devfolio_black_logo.png';
  const githubUrl = 'https://github.com/cjakma?tab=repositories';
  const wifiIconSrc = isDarkMode ? '/icons/wifi_logo_white_22px.png' : '/icons/wifi_logo_black_22px.png';
  const batteryIconSrc = isDarkMode ? '/icons/battery_logo_white_22px.png' : '/icons/battery_logo_black_22px.png';
  const resolvedCreator = creator || 'SSS from ArchiDev';
  const menuTargetByWindow = {
    profile: 'profile-settings',
    resume: 'resume-settings',
    projects: 'projects-settings'
  };
  const activeEditorTarget = focusedWindowId ? menuTargetByWindow[focusedWindowId] : null;
  const menuItems = resolvedOsMode === 'mac' && activeEditorTarget
    ? [
        { id: 'file', label: t(activeLanguage, 'menu_file') },
        { id: 'edit', label: t(activeLanguage, 'menu_edit') },
        { id: 'view', label: t(activeLanguage, 'menu_view') },
        { id: 'go', label: t(activeLanguage, 'menu_go') },
        { id: 'window', label: t(activeLanguage, 'menu_window') },
        { id: 'help', label: t(activeLanguage, 'menu_help') }
      ]
    : [];

  useEffect(() => {
    const timer = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    let batteryManager = null;

    const handleBatteryChange = () => {
      if (!batteryManager) {
        return;
      }

      setBatteryLevel(batteryManager.level ?? 1);
      setIsCharging(Boolean(batteryManager.charging));
    };

    if (!navigator.getBattery) {
      return undefined;
    }

    navigator.getBattery().then((battery) => {
      batteryManager = battery;
      handleBatteryChange();
      battery.addEventListener('levelchange', handleBatteryChange);
      battery.addEventListener('chargingchange', handleBatteryChange);
    });

    return () => {
      if (!batteryManager) {
        return;
      }

      batteryManager.removeEventListener('levelchange', handleBatteryChange);
      batteryManager.removeEventListener('chargingchange', handleBatteryChange);
    };
  }, []);

  useEffect(() => {
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection || null;

    const resolveWifiLabel = () => {
      if (!navigator.onLine) {
        setWifiLabel('Offline');
        return;
      }

      if (!connection) {
        setWifiLabel('Online');
        return;
      }

      const effectiveType = connection.effectiveType || '';
      const downlink = Number(connection.downlink ?? 0);
      const rtt = Number(connection.rtt ?? 0);
      const saveData = Boolean(connection.saveData);
      let level = 4;

      if (effectiveType === 'slow-2g' || effectiveType === '2g') {
        level = 1;
      } else if (effectiveType === '3g') {
        level = 2;
      } else if (effectiveType === '4g') {
        level = downlink >= 10 ? 4 : 3;
      } else if (downlink > 0) {
        if (downlink < 1.5) {
          level = 1;
        } else if (downlink < 5) {
          level = 2;
        } else if (downlink < 10) {
          level = 3;
        }
      }

      if (rtt >= 600) {
        level = Math.min(level, 1);
      } else if (rtt >= 250) {
        level = Math.min(level, 2);
      } else if (rtt >= 120) {
        level = Math.min(level, 3);
      }

      if (saveData) {
        level = Math.max(1, level - 1);
      }

      const labels = ['Offline', 'Weak', 'Fair', 'Good', 'Strong'];
      setWifiLabel(labels[level] || 'Online');
    };

    resolveWifiLabel();
    window.addEventListener('online', resolveWifiLabel);
    window.addEventListener('offline', resolveWifiLabel);
    connection?.addEventListener?.('change', resolveWifiLabel);

    return () => {
      window.removeEventListener('online', resolveWifiLabel);
      window.removeEventListener('offline', resolveWifiLabel);
      connection?.removeEventListener?.('change', resolveWifiLabel);
    };
  }, []);

  const panelBodyStyle = {
    display: 'grid',
    gridTemplateColumns: resolvedOsMode === 'lin' ? 'minmax(0, 1fr)' : 'minmax(0, 1fr) 220px',
    alignItems: 'start',
    gap: '28px'
  };
  const titleLabel = MENU_WINDOW_IDS.includes(focusedWindowId) ? (activeWindowLabel || 'DevFolio') : 'DevFolio';
  const startPanelItems = useMemo(
    () => (Array.isArray(shellShortcutItems) ? shellShortcutItems.slice(0, 6) : []),
    [shellShortcutItems]
  );
  const openStartPanelTarget = (appId) => {
    setIsMenuOpen(false);

    if (appId === 'settings') {
      onOpenSettings?.();
      return;
    }

    onOpenApp?.(appId);
  };
  const startPanelPrimaryItem = startPanelItems.find((item) => item.id === selectedStartItemId)
    || startPanelItems[0]
    || { id: 'settings', label: 'Settings' };
  const startPanelPrimaryLabel = getLocalizedAppLabel(
    startPanelPrimaryItem.id,
    startPanelPrimaryItem.label,
    activeLanguage
  );
  const startPanelPrimaryMeta = WINDOWS_START_ITEM_META[startPanelPrimaryItem.id] || {
    type: 'App',
    description: 'Open this desktop app from the Windows shell.'
  };
  const startPanelApps = startPanelItems.filter((item) => item.id !== startPanelPrimaryItem.id).slice(0, 3);
  const startPanelSettingRows = [
    {
      id: 'settings',
      label: 'Desktop settings',
      icon: 'settings',
      onClick: () => openStartPanelTarget('settings')
    },
    {
      id: 'change-os',
      label: 'Change OS mode',
      icon: 'spark',
      onClick: () => openStartPanelTarget('change-os')
    },
    {
      id: 'ai-auth',
      label: 'Auth Center',
      icon: 'settings',
      onClick: () => openStartPanelTarget('ai-auth')
    }
  ];
  const startPanelActions = [
    {
      id: 'open',
      label: `Open ${startPanelPrimaryLabel}`,
      icon: 'open',
      onClick: () => openStartPanelTarget(startPanelPrimaryItem.id)
    },
    {
      id: 'profile',
      label: 'Open Profile',
      icon: 'spark',
      onClick: () => openStartPanelTarget('profile')
    },
    {
      id: 'resume',
      label: 'Open Resume',
      icon: 'spark',
      onClick: () => openStartPanelTarget('resume')
    },
    {
      id: 'projects',
      label: 'Open Projects',
      icon: 'spark',
      onClick: () => openStartPanelTarget('projects')
    },
    {
      id: 'settings',
      label: 'Desktop settings',
      icon: 'settings',
      onClick: () => openStartPanelTarget('settings')
    }
  ];

  useEffect(() => {
    if (!isMenuOpen || resolvedOsMode !== 'win') {
      return;
    }

    if (startPanelItems.some((item) => item.id === selectedStartItemId)) {
      return;
    }

    setSelectedStartItemId(startPanelItems[0]?.id || 'settings');
  }, [isMenuOpen, resolvedOsMode, selectedStartItemId, startPanelItems]);

  const renderStartPanelGlyph = (item) => {
    const localizedLabel = getLocalizedAppLabel(item.id, item.label, activeLanguage);
    const iconValue = String(item?.icon || '').trim();
    const specialIcon = renderStartPanelSpecialIcon(iconValue);
    if (specialIcon) {
      return specialIcon;
    }

    if (iconValue.includes('/') || iconValue.includes('.')) {
      return <img src={iconValue} alt="" className="windows-start-app-image" aria-hidden="true" />;
    }

    if (START_PANEL_ICON_KEYWORD_PATTERN.test(iconValue)) {
      const fallbackSource = START_PANEL_ICON_FALLBACKS[item.id]?.[0] || `/icons/${iconValue}.svg`;
      return <img src={fallbackSource} alt="" className="windows-start-app-image" aria-hidden="true" />;
    }

    if (item.id === 'change-os') {
      return <span className="windows-start-app-text-badge">OS</span>;
    }

    if (iconValue) {
      return <span className="windows-start-app-emoji" aria-hidden="true">{iconValue}</span>;
    }

    const firstLabel = String(localizedLabel || item.label || '?').trim();
    return <span className="windows-start-app-letter">{firstLabel.slice(0, 1).toUpperCase()}</span>;
  };
  const handleStartPanelAppOpen = (appId) => {
    openStartPanelTarget(appId);
  };
  const windowsStartPanelNode = (
    <div className="topbar-panel topbar-panel-win-start">
      <div className="windows-start-search" role="search" aria-label="Windows start search">
        <span className="windows-start-search-icon" aria-hidden="true">{renderWindowsStartSymbol('search')}</span>
        <span className="windows-start-search-placeholder">{startPanelPrimaryLabel}</span>
      </div>

      <div className="windows-start-tabs" role="tablist" aria-label="Windows search categories">
        {WINDOWS_START_TABS.map((tabLabel, index) => (
          <button
            key={tabLabel}
            type="button"
            className={`windows-start-tab ${index === 0 ? 'active' : ''}`.trim()}
          >
            {tabLabel}
            {tabLabel === 'More' ? <span className="windows-start-inline-icon" aria-hidden="true">{renderWindowsStartSymbol('chevron-down')}</span> : null}
          </button>
        ))}
        <div className="windows-start-tabs-actions">
          <button type="button" className="windows-start-toolbar-button" aria-label="Filter results">
            {renderWindowsStartSymbol('sliders')}
          </button>
          <button type="button" className="windows-start-toolbar-button" aria-label="More actions">
            {renderWindowsStartSymbol('ellipsis')}
          </button>
        </div>
      </div>

      <div className="windows-start-layout">
        <div className="windows-start-results">
          <div className="windows-start-group">
            <h4>Best match</h4>
            <button
              type="button"
              className="windows-start-match-card"
              onClick={() => handleStartPanelAppOpen(startPanelPrimaryItem.id)}
            >
              <span className="windows-start-match-glyph" aria-hidden="true">
                {renderStartPanelGlyph(startPanelPrimaryItem)}
              </span>
              <span className="windows-start-match-copy">
                <strong>{startPanelPrimaryLabel}</strong>
                <span>{startPanelPrimaryMeta.type}</span>
              </span>
            </button>
          </div>

          <div className="windows-start-group">
            <h4>Apps</h4>
            <div className="windows-start-list">
              {startPanelApps.map((item) => (
                <button
                  key={`start-list-${item.id}`}
                  type="button"
                  className={`windows-start-list-item ${selectedStartItemId === item.id ? 'active' : ''}`.trim()}
                  onClick={() => setSelectedStartItemId(item.id)}
                >
                  <span className="windows-start-list-item-main">
                    <span className="windows-start-list-item-glyph" aria-hidden="true">
                      {renderStartPanelGlyph(item)}
                    </span>
                    <span>{getLocalizedAppLabel(item.id, item.label, activeLanguage)}</span>
                  </span>
                  <span className="windows-start-list-item-arrow" aria-hidden="true">
                    {renderWindowsStartSymbol('chevron-right')}
                  </span>
                </button>
              ))}
            </div>
          </div>

          <div className="windows-start-group">
            <h4>Settings</h4>
            <div className="windows-start-list">
              {startPanelSettingRows.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className="windows-start-list-item"
                  onClick={item.onClick}
                >
                  <span className="windows-start-list-item-main">
                    <span className="windows-start-inline-icon windows-start-inline-icon-blue" aria-hidden="true">
                      {renderWindowsStartSymbol(item.icon)}
                    </span>
                    <span>{item.label}</span>
                  </span>
                  <span className="windows-start-list-item-arrow" aria-hidden="true">
                    {renderWindowsStartSymbol('chevron-right')}
                  </span>
                </button>
              ))}
            </div>
          </div>

          <div className="windows-start-web-row">
            <span className="windows-start-inline-icon" aria-hidden="true">{renderWindowsStartSymbol('search')}</span>
            <span>
              {startPanelPrimaryLabel}
              <span className="windows-start-web-muted"> - Explore desktop results</span>
            </span>
          </div>
        </div>

        <div className="windows-start-detail-card">
          <div className="windows-start-detail-hero" aria-hidden="true">
            {renderStartPanelGlyph(startPanelPrimaryItem)}
          </div>
          <strong>{startPanelPrimaryLabel}</strong>
          <span>{startPanelPrimaryMeta.type}</span>
          <p>{startPanelPrimaryMeta.description}</p>
          <div className="windows-start-detail-divider"></div>
          <div className="windows-start-action-list">
            {startPanelActions.map((action) => (
              <button
                key={action.id}
                type="button"
                className="windows-start-action-item"
                onClick={action.onClick}
              >
                <span className="windows-start-inline-icon windows-start-inline-icon-blue" aria-hidden="true">
                  {renderWindowsStartSymbol(action.icon)}
                </span>
                <span>{action.label}</span>
              </button>
            ))}
            <a
              className="windows-start-action-item windows-start-action-link"
              href={githubUrl}
              target="_blank"
              rel="noreferrer"
              onClick={() => setIsMenuOpen(false)}
            >
              <span className="windows-start-inline-icon windows-start-inline-icon-blue" aria-hidden="true">
                {renderWindowsStartSymbol('github')}
              </span>
              <span>{resolvedCreator}</span>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
  const menuButtonNode = resolvedOsMode === 'win' ? (
    <button
      type="button"
      className={`apple-button windows-launcher-button ${isMenuOpen ? 'active' : ''}`.trim()}
      onClick={() => setIsMenuOpen((prev) => !prev)}
      aria-label="Open Windows menu"
    >
      <span className="windows-launcher-grid" aria-hidden="true">
        <span className="windows-launcher-pane"></span>
        <span className="windows-launcher-pane"></span>
        <span className="windows-launcher-pane"></span>
        <span className="windows-launcher-pane"></span>
      </span>
    </button>
  ) : (
    <button
      type="button"
      className={`apple-button ${isMenuOpen ? 'active' : ''}`}
      onClick={() => setIsMenuOpen((prev) => !prev)}
      aria-label="Open DevFolio menu"
    >
      <img src={logoSrc} alt="DevFolio logo" className="topbar-logo" />
    </button>
  );
  const menuWrapperNode = (
    <div className="topbar-menu-wrapper" ref={menuRef}>
      {menuButtonNode}

      {isMenuOpen && resolvedOsMode === 'win' ? windowsStartPanelNode : null}

      {isMenuOpen && resolvedOsMode !== 'win' ? (
        <div className="topbar-panel">
          <div className="panel-header">DevFolio</div>
          <div className="topbar-panel-body" style={panelBodyStyle}>
            <div className="topbar-panel-details">
              <div className="panel-row">
                <span className="panel-label">Creator</span>
                <strong>{resolvedCreator}</strong>
              </div>
              <div className="panel-row">
                <span className="panel-label">Version</span>
                <strong>{version}</strong>
              </div>
            </div>
            <div className="topbar-panel-logo-shell" aria-hidden="true">
              <img src={logoSrc} alt="" className="topbar-panel-logo-large" />
            </div>
          </div>
          <div className="topbar-panel-actions">
            <button type="button" className="panel-settings-button" onClick={() => {
              setIsMenuOpen(false);
              onOpenSettings();
            }}>
              {t(activeLanguage, 'app_settings')}
            </button>
            <a
              className="panel-settings-button topbar-panel-link"
              href={githubUrl}
              target="_blank"
              rel="noreferrer"
              onClick={() => setIsMenuOpen(false)}
            >
              GitHub
            </a>
          </div>
        </div>
      ) : null}
    </div>
  );
  const minimizedWindowButtons = resolvedOsMode === 'win' ? minimizedWindows?.map((app) => (
    <button
      key={`shell-window-${app.id}`}
      type="button"
      className="topbar-window-pill topbar-window-pill-win"
      onClick={() => onRestoreWindow(app.id)}
      title={`${getLocalizedAppLabel(app.id, app.label, activeLanguage)} ${t(activeLanguage, 'restore_suffix')}`}
    >
      {getLocalizedAppLabel(app.id, app.label, activeLanguage)}
    </button>
  )) : null;

  const statusNode = (
    <>
      <label className="topbar-theme-switch" aria-label="Toggle dark mode">
        <input
          type="checkbox"
          checked={isDarkMode}
          onChange={(event) => onToggleTheme(event.target.checked ? 'dark' : 'light')}
        />
        <span className="topbar-theme-switch-track">
          <span className="topbar-theme-switch-thumb"></span>
        </span>
      </label>
      <div className="wifi-status" title={`Wi-Fi ${wifiLabel}`}>
        <img src={wifiIconSrc} alt="" className="topbar-status-image topbar-status-image-wifi" aria-hidden="true" />
        <span className="status-icon">{resolvedOsMode === 'lin' ? wifiLabel.slice(0, 1) : 'Wi-Fi'}</span>
      </div>
      <div className="battery-status" title={isCharging ? 'Charging' : 'Battery'}>
        <img src={batteryIconSrc} alt="" className="topbar-status-image topbar-status-image-battery" aria-hidden="true" />
        <span className="status-icon">{Math.round(batteryLevel * 100)}%</span>
      </div>
      <span className="clock">
        {resolvedOsMode === 'lin' ? time.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }) : formatTopBarClock(time, activeLanguage)}
      </span>
    </>
  );

  return (
    <div className={`top-bar glass-effect shell-bar shell-bar-${resolvedOsMode} ${isDarkMode ? 'dark' : 'light'}`.trim()}>
      {resolvedOsMode === 'mac' ? <span className="topbar-colorbar" aria-hidden="true"></span> : null}
      <div className="left-menu shell-group shell-group-primary">
        {resolvedOsMode !== 'win' ? menuWrapperNode : null}

        {resolvedOsMode !== 'lin' ? (
          <span className={`menu-item bold shell-title ${resolvedOsMode !== 'mac' ? 'shell-title-compact' : ''}`.trim()}>
            {titleLabel}
          </span>
        ) : null}
        {resolvedOsMode === 'lin' ? shellShortcutNode : null}
        {menuItems.map((item) => (
          <button
            key={item.id}
            type="button"
            className={`menu-item menu-item-button menu-item-button-${item.id} ${item.id === 'file' && hasAuthenticatedAiSession ? 'menu-item-button-ai-ready' : ''}`.trim()}
            onClick={() => onProtectedMenuAction(item.id, activeEditorTarget)}
          >
            {item.label}
          </button>
        ))}
      </div>

      <div className={`topbar-center-menu shell-group shell-group-center ${resolvedOsMode === 'lin' ? 'shell-group-center-vertical' : ''}`.trim()} aria-label="Minimized windows">
        {resolvedOsMode === 'win' ? (
          <>
            {menuWrapperNode}
            {shellShortcutNode}
          </>
        ) : minimizedWindowButtons}
      </div>

      <div className={`right-menu shell-group shell-group-status ${resolvedOsMode === 'lin' ? 'shell-group-status-vertical' : ''}`.trim()}>
        {statusNode}
      </div>
    </div>
  );
};

export default TopBar;
